
import {
  defineComponent,
  PropType,
  Ref,
  ref,
  watch,
  computed,
  onBeforeMount,
  onMounted,
} from 'vue';

import { useStore } from '@/store';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';

import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import FileManager from '@/components/file-manager/FileManager.vue';
import EditImageModal from '@/components/modals/EditImageModal.vue';
import ToggleDisplaySettings from '@/components/template-builder/fields/partials/ToggleDisplaySettings.vue';
import BackgroundImageOption from '@/components/template-builder/fields/partials/BackgroundImageOption.vue';

import {
  FieldConfig,
  File,
  ParserFieldObject,
  Property,
  BackgroundImageOptionTypeEnum,
} from '@/types';

import {
  detectMonochromeImageCached,
  getColorizedUrl,
  getColorizedUrlInfo,
  isColorizableUrl,
  MonochromeDetectionResult,
} from '@/components/template-builder/utils/image-color-detection';

import Color from '@/components/template-builder/fields/Color.vue';

// eslint-disable-next-line import/no-cycle
import {
  TemplateEditorState as state,
} from '@/composables/template-editor/TemplateEditor';

import { useI18n } from 'vue-i18n';
import { removeImage } from '../callbacks';
import { removeDynamicStyle } from '../utils/parser';
import { isEmailTemplate } from '../utils/helpers';

export default defineComponent({
  name: 'BackgroundImage',

  components: {
    FileManager,
    EditImageModal,
    Button,
    InputText,
    ToggleDisplaySettings,
    BackgroundImageOption,
    BaseDropdown,
    Color,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    tabItemLabel: {
      type: String,
      required: true,
    },

    fieldGroupLabel: {
      type: String,
      required: true,
    },

    elementPartType: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const store = useStore();

    const mounted = ref(false);

    const displayImageModal = ref(false);
    const editImageModal = ref(false);

    const displayProperties = ref(!(!Object.prototype.hasOwnProperty.call(props.configs, 'displayToggle') || props.configs.displayToggle === true));
    const displayConfigs = ref(props.configs);

    const srcProperty: Ref<Property> = ref(props.parserValues.properties && props.parserValues.properties.filter((property) => property.name === 'background-image')[0]);
    const selectedImage: Ref<string> = ref(srcProperty.value.value.match(/url\("([^"]+)"\)/) ? srcProperty.value.value.match(/url\("([^"]+)"\)/)[1] : '');
    const savedSelectedImage: Ref<string> = ref(selectedImage.value);

    const isForSection = ref(
      props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR
      || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_OPTIONS
      || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_SIZE_REPEAT,
    );
    const noOptions = ref(props.configs.options.type === BackgroundImageOptionTypeEnum.NO_OPTION);
    const hasOptions = ref(
      props.configs.options.type === BackgroundImageOptionTypeEnum.FULL
      || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_OPTIONS
      || props.configs.options.type === BackgroundImageOptionTypeEnum.SIZE_REPEAT
      || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_SIZE_REPEAT,
    );

    const selectedSectionOption = ref(props.configs.options.defaultValue ?? '');
    const lastSelectedSectionOption = ref(selectedSectionOption.value);
    const sectionOptions = computed(() => {
      if (props.configs.options.options) {
        return props.configs.options.options.map((option: any) => ({
          label: t(option.label),
          value: option.value,
        }));
      }
      return [];
    });

    const isRemoved = ref(selectedImage.value.trim() === '');

    // Monochrome detection state
    const isMonochromeImage: Ref<boolean> = ref(false);

    // Initialize color from existing colorized URL (synchronous)
    const getInitialColor = (): string => {
      if (selectedImage.value) {
        const colorInfo = getColorizedUrlInfo(selectedImage.value);
        if (colorInfo.isAlreadyColorized && colorInfo.currentColor) {
          return colorInfo.currentColor;
        }
      }
      return '#000000';
    };
    const monochromeColor: Ref<string> = ref(getInitialColor());

    // Configuration for Color.vue component
    const colorFieldConfig = computed(() => ({
      label: 'templateBuilder.fields.imageColor',
      displayToggle: false,
      options: {
        defaultValue: monochromeColor.value,
        showAlpha: false,
        showGradient: false,
      },
      properties: [{
        name: 'imageColor',
        value: monochromeColor.value,
        getter: () => monochromeColor.value,
        setters: [],
      }],
    }));

    const colorParserValues = computed(() => ({
      selector: props.parserValues.selector,
      properties: [{
        name: 'imageColor',
        value: monochromeColor.value,
        getter: () => monochromeColor.value,
        setters: [],
      }],
    }));

    const handleColorChangeFromColor = (event: { properties: Property[] }) => {
      if (event.properties && event.properties[0]) {
        const newColor = event.properties[0].value as string;

        // Only accept 6-character hex colors (e.g., #FF0000)
        const hex6Regex = /^#[A-Fa-f0-9]{6}$/;
        if (!hex6Regex.test(newColor)) {
          return;
        }

        monochromeColor.value = newColor;

        // Generate colorized URL and apply it
        const colorizedUrl = getColorizedUrl(selectedImage.value, newColor);
        if (colorizedUrl) {
          selectedImage.value = colorizedUrl;
          handleImageChange();
        }
      }
    };

    const analyzeImage = async (imageUrl: string) => {
      if (!imageUrl || imageUrl.includes('placeholder')) {
        isMonochromeImage.value = false;
        return;
      }

      // Check if URL is colorizable first
      if (!isColorizableUrl(imageUrl)) {
        isMonochromeImage.value = false;
        return;
      }

      // Get original URL for detection (in case it's already colorized)
      const { originalUrl } = getColorizedUrlInfo(imageUrl);
      const colorInfo = getColorizedUrlInfo(imageUrl);

      // If already colorized, use the existing color
      if (colorInfo.isAlreadyColorized && colorInfo.currentColor) {
        monochromeColor.value = colorInfo.currentColor;
      }

      try {
        const urlToAnalyze = originalUrl || imageUrl;
        const result: MonochromeDetectionResult = await detectMonochromeImageCached(urlToAnalyze);
        isMonochromeImage.value = result.isMonochrome;
        if (result.isMonochrome && !colorInfo.isAlreadyColorized) {
          // Re-apply pending color from previous monochrome image
          if (pendingColor.value) {
            monochromeColor.value = pendingColor.value;
            const colorizedUrl = getColorizedUrl(imageUrl, pendingColor.value);
            if (colorizedUrl) {
              selectedImage.value = colorizedUrl;
              handleImageChange();
            }
            pendingColor.value = null;
          } else if (result.detectedColor) {
            monochromeColor.value = result.detectedColor;
          }
        } else {
          pendingColor.value = null;
        }
      } catch (error) {
        console.warn('[BackgroundImage] Monochrome detection failed:', error);
        isMonochromeImage.value = false;
        pendingColor.value = null;
      }
    };

    const saveProperties = (imageValue: string, init = false) => {
      // Remove last selectedSectionOption
      if (props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR
        || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_OPTIONS
        || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_SIZE_REPEAT) {
        srcProperty.value.value = 'none';
        let lastSelectedSelector = props.parserValues.selector;
        if (lastSelectedSectionOption.value === '.container') {
          lastSelectedSelector = `${lastSelectedSelector} ${selectedSectionOption.value}`;
        }
        context.emit('on-change-properties', {
          selector: lastSelectedSelector,
          properties: [srcProperty.value],
          init,
        });
      }

      // Change with new value
      let selectedSelector = props.parserValues.selector;
      srcProperty.value.value = `url("${imageValue}")`;
      if (imageValue.trim() === '') {
        srcProperty.value.value = 'none';

        const selectedStructure = store.getters['liveEditor/getSelectedStructure'];
        if (selectedStructure && props.configs.linkedItem) {
          const linkedParserValues = selectedStructure.parserValues[props.tabItemLabel].items[props.fieldGroupLabel][props.configs.linkedItem];
          if (linkedParserValues) {
            const backgroundProperty = ref(linkedParserValues.properties && linkedParserValues.properties.filter((property: any) => property.name === 'background')[0]);
            if (backgroundProperty.value) {
              backgroundProperty.value.value = backgroundProperty.value.value.replace(/url\("([^"]+)"\)/, '');
              context.emit('on-change-properties', {
                selector: selectedSelector,
                properties: [backgroundProperty.value],
                init,
              });
            }
          }
        }
      }

      // Update selector only if in section
      if (selectedSectionOption.value === '.container'
        && (props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR
          || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_OPTIONS
          || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_SIZE_REPEAT)) {
        selectedSelector = `${selectedSelector} ${selectedSectionOption.value}`;
      }

      context.emit('on-change-properties', {
        selector: selectedSelector,
        properties: [srcProperty.value],
        init,
      });
    };

    const handleImageChange = () => {
      savedSelectedImage.value = selectedImage.value;
      if (selectedImage.value.trim() === '' && isEmailTemplate(state.template?.type)) {
        removeDynamicStyle(props.parserValues.selector, { 'background-image': 'background-image' });
      }
      saveProperties(selectedImage.value);
      isRemoved.value = selectedImage.value.trim() === '';
    };

    const handleRemove = () => {
      selectedImage.value = '';
      removeImage(props.parserValues.selector);
      handleImageChange();
    };

    // Pending color to re-apply after monochrome detection confirms
    const pendingColor: Ref<string | null> = ref(null);

    const onChooseImage = (image: File) => {
      // Save current color to re-apply if new image turns out to be monochrome
      if (isMonochromeImage.value && monochromeColor.value && monochromeColor.value !== '#000000') {
        pendingColor.value = monochromeColor.value;
      }

      selectedImage.value = image.url;
      handleImageChange();
    };

    const onSaveImage = (imageUrl: string) => {
      selectedImage.value = imageUrl;
      handleImageChange();
    };

    const handleOptionChange = (property: Property, init = false) => {
      // Use the correct selector based on section option (full-width or .container)
      let selectedSelector = props.parserValues.selector;
      if (selectedSectionOption.value === '.container'
        && (props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR
          || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_OPTIONS
          || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_SIZE_REPEAT)) {
        selectedSelector = `${selectedSelector} ${selectedSectionOption.value}`;
      }
      context.emit('on-change-properties', {
        selector: selectedSelector,
        properties: [property],
        init,
      });
    };

    watch(() => displayProperties.value, () => {
      if (!displayProperties.value) {
        savedSelectedImage.value = selectedImage.value;
        saveProperties('');
      } else {
        // setTimeout to avoids undesirable visual effects
        const init = !mounted.value;
        setTimeout(async () => {
          saveProperties(savedSelectedImage.value, init);
        }, 100);
      }
    }, { deep: true });

    watch(() => selectedSectionOption.value, (newValue, oldValue) => {
      if (props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR
        || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_OPTIONS
        || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_SIZE_REPEAT) {
        lastSelectedSectionOption.value = oldValue;
        saveProperties(selectedImage.value);
      }
    }, { deep: true });

    onBeforeMount(() => {
      if (props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR
        || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_OPTIONS
        || props.configs.options.type === BackgroundImageOptionTypeEnum.SELECTOR_WITH_SIZE_REPEAT) {
        const backgroundProperty = props.parserValues.properties.find((property) => property.name === 'backgroundImageCustomSelector');
        if (backgroundProperty && backgroundProperty.value.includes('.container')) {
          selectedSectionOption.value = '.container';
        } else {
          selectedSectionOption.value = 'full-width';
        }
      }
    });

    onMounted(() => {
      mounted.value = true;
      // Analyze image on mount
      if (selectedImage.value) {
        analyzeImage(selectedImage.value);
      }
    });

    // Watch for image changes to re-analyze
    watch(() => selectedImage.value, (newImage) => {
      if (newImage) {
        analyzeImage(newImage);
      } else {
        isMonochromeImage.value = false;
      }
    });

    return {
      t,
      displayImageModal,
      editImageModal,
      selectedImage,
      displayProperties,
      isForSection,
      selectedSectionOption,
      sectionOptions,
      displayConfigs,
      isRemoved,
      noOptions,
      hasOptions,

      // Monochrome detection
      isMonochromeImage,
      colorFieldConfig,
      colorParserValues,
      handleColorChangeFromColor,

      onChooseImage,
      onSaveImage,
      handleImageChange,
      handleRemove,
      handleOptionChange,
    };
  },
});
